import { storeService } from '@/services';
import DeliveryChargeVM from '@/view-model/delivery-charge/DeliveryChargeVM';
import { vuexEventPathGenerator } from '@/utils';
import { FormDataDeliveryCharge } from '@/form/form-data/FormDataDeliveryCharge';
import { DeliveryChargeDelete } from '@/service-data/delivery-charge/DeliveryChargeDelete';
import { DeliveryChargePut } from '@/service-data/delivery-charge/DeliveryChargePut';
import { DeliveryChargePost } from '@/service-data/delivery-charge/DeliveryChargePost';
import { objDeepCompare } from '@/utils/object';
import { mutationType } from './mutations';
export const actionType = {
    setToggleForm: 'setToggleForm',
    dialogOpen: 'dialogOpen',
    dialogClose: 'dialogClose',
    areaDialogOpen: 'areaDialogOpen',
    areaDialogClose: 'areaDialogClose',
    /** 구역선택 */
    selectedArea: 'selectedArea',
    /** 리스트 조회 */
    getDeliveryChargeList: 'getDeliveryChargeList',
    /** 상점 정보 셋팅 */
    setStoreData: 'setStoreData',
    setPageNumber: 'setPageNumber',
    reset: 'reset',
    setFilterOption: 'setFilterOption',
    setCheckedList: 'setCheckedList',
    deleteCheckedItem: 'deleteCheckedItem',
    submit: 'submit',
    /** form 특정 필드 업데이트 */
    updateFormData: 'updateFormData',
    /** 수정 취소 (선택 취소) */
    cancelEdit: 'cancelEdit',
    /** 배달대행료 row 선택 */
    selectItem: 'selectItem',
    /** origin form 셋팅 */
    setOriginForm: 'setOriginForm',
    /** 검색 */
    onSearch: 'onSearch',
    /** 비교 처리 진행 */
    confirmChanged: 'confirmChanged',
};
export const deliveryChargeActionType = vuexEventPathGenerator(actionType, 'deliveryChargeDialog');
const action = {
    async [actionType.confirmChanged]({ state }, confirm) {
        const originForm = state.originFormData;
        const currentForm = state.formData;
        const isSame = objDeepCompare(originForm, currentForm);
        // 기존 formData, 현재 formData 같으면 done.
        if (isSame) {
            throw new Error();
        }
        // 값 다를 경우 컨펌창 출력
        if (!isSame) {
            // 수정 사항 취소 여부
            const isConfirm = await confirm();
            // 수정 사항 취소 ok
            if (isConfirm) {
                throw new Error();
            }
            // 수정 사항 취소 no -> 동작 없음 done.
        }
    },
    /**
     * 검색 액션
     * 검색은 페이지네이션을 1로 변경해야 한다.
     */
    [actionType.onSearch]({ commit, dispatch }) {
        commit(mutationType.SET_PAGE_NUMBER, 1);
        dispatch(actionType.getDeliveryChargeList);
    },
    [actionType.setOriginForm]({ commit, state }) {
        const originForm = new FormDataDeliveryCharge(state.formData);
        commit(mutationType.SET_ORIGIN_FORM, originForm);
    },
    async [actionType.cancelEdit]({ state, commit }) {
        const getForm = () => new FormDataDeliveryCharge({
            storeName: state.storeDetail?.name,
        });
        commit(mutationType.SET_FORM, getForm());
        commit(mutationType.SET_ORIGIN_FORM, getForm());
        commit(mutationType.IS_EDIT, false);
        commit(mutationType.SELECTED_ITEM);
        commit(mutationType.SELECTED_AREA);
        commit(mutationType.RESET_FORM_VALIDATION);
    },
    async [actionType.selectItem]({ commit, state }, selectedItemId) {
        const item = state
            .deliveryChargeList
            .find(({ id }) => id === selectedItemId);
        const getForm = () => new FormDataDeliveryCharge({
            storeName: state.storeDetail?.name,
            areaId: item.areaId,
            id: item.id,
            addressJibunEnd: item.addressJibunEnd,
            addressJibunStart: item.addressJibunStart,
            areaName: item.areaName,
            businessHourEnd: item.businessHourEnd,
            businessHourStart: item.businessHourStart,
            charge: item.charge,
            distanceEnd: item.distanceEnd,
            distanceStart: item.distanceStart,
            specialArea: item.specialArea,
            surcharge: item.surcharge,
            workingDay: item.workingDay,
            type: item.type,
        });
        commit(mutationType.SET_FORM, getForm());
        commit(mutationType.SET_ORIGIN_FORM, getForm());
        commit(mutationType.IS_EDIT, true);
        commit(mutationType.SELECTED_ITEM, item);
        commit(mutationType.RESET_FORM_VALIDATION);
    },
    async [actionType.updateFormData]({ commit }, payload) {
        commit(mutationType.UPDATE_FORM_DATA, payload);
    },
    async [actionType.submit]({ state }) {
        const { storeDetail, formData, isEdit, } = state;
        const { id: storeId, } = storeDetail;
        // 수정
        if (isEdit) {
            const serviceData = new DeliveryChargePut({
                ...formData,
                storeId,
            });
            return storeService.putDeliveryCharge(serviceData);
        }
        // 등록
        const serviceData = new DeliveryChargePost({
            ...formData,
            storeId,
        });
        return storeService.postDeliveryCharge(serviceData);
    },
    async [actionType.deleteCheckedItem]({ state, commit, dispatch, }) {
        const { storeDetail, } = state;
        const { id: storeId, } = storeDetail;
        const serviceData = new DeliveryChargeDelete({
            ...state.formData,
            storeId,
            checkedList: state.checkedList,
        });
        await storeService.deleteDeliveryChargeList(serviceData);
        const selectedItemId = state.selectedItem?.id;
        const result = state.checkedList.findIndex((id) => String(id) === String(selectedItemId));
        if (result >= 0) {
            await dispatch(actionType.setToggleForm);
            await dispatch(actionType.cancelEdit);
        }
        commit(mutationType.CHECKED_LIST);
    },
    [actionType.setCheckedList]({ commit }, payload) {
        commit(mutationType.CHECKED_LIST, payload);
    },
    [actionType.setFilterOption]({ commit }, payload) {
        commit(mutationType.SET_FILTER_OPTION, payload);
    },
    [actionType.reset]({ commit }) {
        commit(mutationType.RESET);
    },
    [actionType.setStoreData]({ commit }, storeData) {
        commit(mutationType.SET_STORE_DETAIL, storeData);
        commit(mutationType.UPDATE_FORM_DATA, {
            key: 'storeName',
            value: storeData.name,
        });
    },
    [actionType.dialogOpen]({ commit }) {
        commit(mutationType.DIALOG_OPEN);
    },
    [actionType.dialogClose]({ commit }) {
        commit(mutationType.DIALOG_CLOSE);
    },
    [actionType.areaDialogOpen]({ commit }) {
        commit(mutationType.AREA_DIALOG_OPEN);
    },
    [actionType.areaDialogClose]({ commit }) {
        commit(mutationType.AREA_DIALOG_CLOSE);
    },
    [actionType.selectedArea]({ commit }, item) {
        commit(mutationType.SELECTED_AREA, item);
    },
    async [actionType.getDeliveryChargeList]({ state, commit, }) {
        try {
            const { storeDetail, filterOption, paginationData, } = state;
            const { id: storeId } = storeDetail;
            const { pageSizeType, searchWord, } = filterOption;
            const { pageNumber } = paginationData;
            // 필터
            const filters = [];
            // 검색
            // filters.push(...getters.getterSearchFieldTypeParam);
            const pageSize = pageSizeType;
            const param = {
                pageNumber,
                pageSize,
                filters,
                storeId,
                searchWord,
            };
            const data = await storeService.getDeliveryChargeList(param);
            commit(mutationType.GET_DELIVERY_CHARGE_LIST, {
                paginationData: data.paginationData,
                list: data.list.map((delvieryCharge) => new DeliveryChargeVM(delvieryCharge)),
            });
        }
        catch (error) {
            console.log('[hubManagement/hubList/getHubList]', error);
        }
    },
    [actionType.setPageNumber]({ commit }, pageNumber) {
        commit(mutationType.SET_PAGE_NUMBER, pageNumber);
    },
    /** true 출력, false 숨김 */
    [actionType.setToggleForm]({ commit }, flag = false) {
        commit(mutationType.SET_TOGGLE_FORM, flag);
    },
};
export default action;
